import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, InputNumber, App } from 'antd';
import { Table, CreateTableRequest, UpdateTableRequest, Area } from '../../../types/tables';

interface TableFormModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateTableRequest | UpdateTableRequest) => Promise<void>;
  table?: Table;
  areas: Area[];
  selectedAreaId?: string | null;
  loading?: boolean;
}

const TableFormModal: React.FC<TableFormModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  table,
  areas,
  selectedAreaId,
  loading = false,
}) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const isEditing = !!table;

  useEffect(() => {
    if (visible) {
      if (isEditing && table) {
        form.setFieldsValue({
          number: table.number,
          areaId: table.areaId,
          capacity: table.capacity,
          x: table.x,
          y: table.y,
        });
      } else {
        // Para nueva mesa, preseleccionar el área activa si existe
        form.resetFields();
        if (selectedAreaId) {
          form.setFieldsValue({
            areaId: selectedAreaId,
          });
        }
      }
    }
  }, [visible, isEditing, table, selectedAreaId, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Asegurar que X e Y se envíen como null si no tienen valor
      const submitData = {
        ...values,
        x: values.x ?? null,
        y: values.y ?? null,
      };
      await onSubmit(submitData);
      form.resetFields();
      message.success(isEditing ? 'Mesa actualizada exitosamente' : 'Mesa creada exitosamente');
    } catch (error) {
      console.error('Error al enviar formulario:', error);
    }
  };

  const areaOptions = areas.map(area => ({
    value: area.id,
    label: area.name,
  }));

  return (
    <Modal
      title={isEditing ? 'Editar Mesa' : 'Nueva Mesa'}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
      >
        <Form.Item
          name="number"
          label="Número de Mesa"
          rules={[
            { required: true, message: 'El número de mesa es requerido' },
            { max: 10, message: 'El número no puede exceder 10 caracteres' },
          ]}
        >
          <Input placeholder="Ej: 1, A1, VIP-1" />
        </Form.Item>

        <Form.Item
          name="areaId"
          label="Área"
          rules={[{ required: true, message: 'El área es requerida' }]}
        >
          <Select
            placeholder="Seleccionar área"
            options={areaOptions}
          />
        </Form.Item>

        <Form.Item
          name="capacity"
          label="Capacidad"
          rules={[
            { required: true, message: 'La capacidad es requerida' },
            { type: 'number', min: 1, max: 20, message: 'La capacidad debe estar entre 1 y 20' },
          ]}
        >
          <InputNumber
            placeholder="Número de personas"
            style={{ width: '100%' }}
            min={1}
            max={20}
          />
        </Form.Item>

        {/* Campos de coordenadas X,Y ocultos - no se usan por ahora */}
        <Form.Item name="x" hidden>
          <InputNumber />
        </Form.Item>

        <Form.Item name="y" hidden>
          <InputNumber />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TableFormModal;